import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getErrorMessage } from '@common/utils/getErrorDetails';
import { toast } from 'react-toastify';
import { truncateFileName } from '@common/utils';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import { useMutateDeleteMedia } from '@/reactQuery/post';

interface DeleteQuestionMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  questionId: string;
  mediaName: string;
  onSuccess: () => void;
  mediaId: string;
  scope?: 'interview' | 'screening';
}

function DeleteQuestionMediaModal({
  isOpen,
  onClose,
  questionId,
  mediaName,
  onSuccess,
  mediaId,
  scope,
}: DeleteQuestionMediaModalProps) {
  const { lang } = useTranslation();
  const {
    organization_id: organizationId,
    workspace_id: workspaceId,
    project_id: projectId,
  } = useParams();
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutateAsync: mutateAsyncDeleteMedia } = useMutateDeleteMedia(
    questionId,
    organizationId,
    workspaceId,
    projectId,
    scope,
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await mutateAsyncDeleteMedia({ deleteMediaId: mediaId });
      toast.success(lang.get('msg.mediaSuccessfullyDeleted'));
      onClose();
      onSuccess();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{lang.get('msg.deleteMedia')}</AlertDialogTitle>
          <AlertDialogDescription>
            {lang.get('msg.areYouSureYouWantToDeleteMedia')}:{' '}
            <span className="font-semibold break-all" title={mediaName}>
              {truncateFileName(mediaName)}
            </span>
            ?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-4">
          <AlertDialogCancel onClick={onClose}>{lang.get('msg.cancel')}</AlertDialogCancel>
          <Button
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700"
            disabled={isDeleting}
          >
            {isDeleting ? lang.get('msg.deleting') : lang.get('msg.delete')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DeleteQuestionMediaModal;
